import type { BookSummary } from '../models';
import type { BookSortBy, SortOrder } from './filters';

// In-memory equivalent of Flutter's library ORDER BY (drift_audiobook_repository
// `_sqlOrderByForFilters` / RemoteBookRepository `_compareBooks`). Text keys
// compare lower-cased by code unit (Dart `compareTo`); ties fall back to the
// title, then the id; descending reverses the whole comparison.

type Key = number | string;

const lower = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

function sortKey(book: BookSummary, sortBy: BookSortBy): Key {
  switch (sortBy) {
    case 'dateAdded':
      return book.addedAt;
    case 'title':
      return lower(book.title);
    case 'duration':
      return book.duration;
    case 'author':
      return lower(book.authorName);
    case 'narrator':
      return lower(book.narrators[0]);
    case 'dateReleased':
      return book.publishedYear ?? 0;
    case 'lastPlayed':
      return book.lastUpdate ?? 0;
    case 'fileSize':
      return book.sizeBytes ?? 0;
  }
}

function compareKeys(a: Key, b: Key): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

export function compareBooks(a: BookSummary, b: BookSummary, sortBy: BookSortBy): number {
  return compareKeys(sortKey(a, sortBy), sortKey(b, sortBy)) || compareKeys(lower(a.title), lower(b.title)) || compareKeys(a.id, b.id);
}

/** Sorted copy; all eight Flutter sort options, case-insensitive. */
export function sortBooks(books: readonly BookSummary[], sortBy: BookSortBy, sortOrder: SortOrder): BookSummary[] {
  const dir = sortOrder === 'ascending' ? 1 : -1;
  return [...books].sort((a, b) => dir * compareBooks(a, b, sortBy));
}
